import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Auth } from '@angular/fire/auth';
import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  Firestore,
  QueryDocumentSnapshot,
  QueryFieldFilterConstraint,
  QuerySnapshot,
  addDoc,
  collection,
  collectionData,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  deleteDoc,
  setDoc,
  where,
} from '@angular/fire/firestore';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, map, tap } from 'rxjs';
import {
  Address,
  Invoice,
  Issue,
  Machine,
  Servicing,
  Transaction,
  User,
} from './repository/models/user-profile.interfaces';
import {
  ColumnDefinition,
  CustomUser,
  DropdownValueGetter,
  IDeptDefinition,
  InputFieldData,
  KeyValueArray,
  KeyValueGroupArray,
  RecursiveCollectionDefinition,
} from '@res/ngg-core';

export type TableList<T> = DocumentData & { users: T[]; page: number; pagesize: number; totalElements: number };
export interface ClientsData extends DocumentData {
  type: string;
  headquarterAddress: string;
  companyName: string;
  displayName: string;
  phoneNumber: string;
  position: string;
  id: string;
  email: string;
  photoUrl: string;
}
export type FilterCriteria<T> = Record<keyof T, string | number | Date | boolean>;

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  public async getUserIdFrom(elementType: string, id: string): Promise<string> {
    switch (elementType) {
      case 'addresses':
        return (await getDocs(query(this.getAddressesCollectionGroupRef(), where('id', '==', id)))).docs[0].id;
      case 'issues':
        return ((await getDoc(doc(this.IssuesCollectionRef, id))).data()?.['userId'] as DocumentReference).id;
      case 'servicing': {
        const machineRef = (await getDocs(query(this.ServicingCollectionRef, where('id', '==', id)))).docs[0].data()[
          'belongsTo'
        ] as DocumentReference;
        return ((await getDoc(machineRef)).data()?.['userId'] as DocumentReference).id;
      }
      case 'machines':
        return ((await getDoc(doc(this.MachinesCollectionRef, id))).data()?.['userId'] as DocumentReference).id;
      case 'transactions':
        throw Error('not implemented');
      // return ((await getDocs(query(this.TransactionsCollectionRef, where('id', '==', id)))).docs[0].data()['userId'] as DocumentReference).id
      case 'invoces':
        throw Error('not implemented');
      // return ((await getDocs(query(this.InvoicesCollectionRef, where('id', '==', id)))).docs[0].data()['userId'] as DocumentReference).id
      default:
        return id;
    }
  }
  addNewDocByType(
    type: string,
    data: Address | User | Issue | Machine | Transaction | Invoice | Servicing,
    userId: string,
  ) {
    switch (type) {
      case 'addresses':
        return this.addNewUserAddressDoc(data as Address, userId);
      case 'users':
        return this.addNewUserDoc(data as User);
      case 'issues':
        return this.addNewIssuesDoc(data as Issue);
      case 'servicing':
        return this.addNewServicingDoc(data as Servicing);
      case 'machines':
        return this.addNewMachinesDoc(data as Machine);
      case 'transactions':
        return this.addNewTransactionsDoc(data as Transaction);
      case 'invoces':
        return this.addNewInvoicesDoc(data as Invoice);
      default:
        return undefined;
    }
  }
  public async getCalendarDataForAdmin(): Promise<{
    serviceData: Array<DocumentData>;
    issueData: Array<DocumentData>;
  }> {
    if (this.authService.currentUser?.uid) {
      const serviceData = (await getDocs(this.ServicingCollectionRef)).docs.map((x) => x.data());
      const issueData = (await getDocs(this.IssuesCollectionRef)).docs.map((x) => x.data());
      return { serviceData, issueData };
    }
    {
      return { serviceData: [], issueData: [] };
    }
  }
  public async getCalendarDataForNonAdmin(): Promise<{
    serviceData: Array<DocumentData>;
    issueData: Array<DocumentData>;
  }> {
    if (this.authService.currentUser?.uid) {
      const userDocReference = doc(this.UsersCollectionRef, this.authService.currentUser.uid);
      const userWhereForMachines = where('userId', '==', userDocReference);
      const qMachines = query(this.MachinesCollectionRef, userWhereForMachines);
      const machineDocs = (await getDocs(qMachines)).docs;

      // const machinesWhereForIssues = machineDocs.map((x) => where('belongsTo', '==', x.ref));

      // const qServices = query(this.ServicingCollectionRef, or(...machinesWhereForIssues));
      const userWhereForIssues = where('userId', '==', userDocReference);
      const qIssues = query(this.IssuesCollectionRef, userWhereForIssues);

      const issueData = (await getDocs(qIssues)).docs.map((x) => x.data());
      const servicesData = await this.getServicesDataByMachineRefs(machineDocs.map((x) => x.ref));
      // const serviceData = (await getDocs(qServices)).docs.map((x) => x.data());
      return { serviceData: [...servicesData.flat()], issueData };
    }
    {
      return { serviceData: [], issueData: [] };
    }
  }
  private async getServicesDataByMachineRefs(refs: DocumentReference<DocumentData, DocumentData>[]) {
    return await Promise.all(
      refs.map(async (x) => {
        const w = where('belongsTo', '==', doc(this.MachinesCollectionRef, x.id));
        const q = query(this.ServicingCollectionRef, w);
        const docs = (await getDocs(q)).docs;
        if (docs?.length) {
          return docs.map((x) => x.data());
        } else {
          return [];
        }
      }),
    );
  }
  public async getFieldValues(
    collectionName: string,
    dropdownValues: string,
  ): Promise<Array<DocumentData & { parent: DocumentReference }>> {
    const collGroup =
      collectionName === 'addresses'
        ? collectionGroup(this.firestore, collectionName)
        : collection(this.firestore, collectionName);
    const q = query(collGroup, orderBy(dropdownValues, 'asc'));
    const docsData = await getDocs(q);
    return docsData.docs.map((x) => ({ ...x.data(), parent: x?.ref }));
  }
  private tlocs = inject(TranslocoService);
  private empty$ = this.tlocs.selectTranslate('empty');
  private empty = toSignal(this.empty$);
  private authService = inject(Auth);

  public listCustomers(ops?: { idField: string }): Observable<ClientsData[]> {
    return collectionData(this.UsersCollectionRef, ops).pipe(
      tap(console.log),
      map((x) => x),
    );
  }

  // skipcq: JS-0105
  fuzzysearch(needle: string, haystack: string) {
    const hlen = haystack.length;
    const nlen = needle.length;
    if (nlen > hlen) {
      return false;
    } else if (nlen === 0) {
      return true;
    }
    if (nlen === hlen) {
      return needle === haystack;
    }
    outer: for (let i = 0, j = 0; i < nlen; i++) {
      const nch = needle.charCodeAt(i);
      while (j < hlen) {
        if (haystack.charCodeAt(j++) === nch) {
          continue outer;
        }
      }
      return false;
    }
    return true;
  }

  public async getCollectionDataForNonAdmin(
    collectionRef: CollectionReference<DocumentData, DocumentData>,
    lookupField?: RecursiveCollectionDefinition,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<QueryDocumentSnapshot<DocumentData, DocumentData>[]> {
    const lookupCollection = await this.getRecursivCollectionQuery(lookupField);
    const q = query(collectionRef, lookupCollection);
    return (await getDocs(q)).docs;
  }

  private async getRecursivCollectionQuery(
    lookupField?: RecursiveCollectionDefinition | undefined,
  ): Promise<QueryFieldFilterConstraint> {
    if (!lookupField) {
      const currentUser = this.authService.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user');
      }
      return where('userId', '==', doc(this.UsersCollectionRef, currentUser.uid));
    }
    const querryData = await this.getRecursivCollectionQuery(lookupField?.subCollectionField);
    const collRef = collection(this.firestore, lookupField.collection);
    const q = query(collRef, querryData);
    const docsData = await getDocs(q);
    return where(lookupField.field, 'in', [...docsData.docs.map((data) => data.ref)]);
  }

  public async getDocsForNonAdmin(
    collectionRef: CollectionReference<DocumentData, DocumentData>,
  ): Promise<QuerySnapshot<unknown, DocumentData>> {
    const userWhere = where('userId', '==', `users/${this.authService.currentUser?.uid}`);
    const q = query(collectionRef, userWhere);
    return await getDocs(q);
  }

  private firestore = inject(Firestore);

  // skipcq: JS-0105
  get timestamp() {
    return serverTimestamp();
  }

  get UsersCollectionRef() {
    return collection(this.firestore, 'users');
  }

  public getAddressesCollectionRef(userId: string) {
    return collection(this.firestore, `users/${userId}/addresses`);
  }

  public getAddressesCollectionGroupRef() {
    return collectionGroup(this.firestore, '/addresses');
  }

  get ServicingCollectionRef() {
    return collection(this.firestore, 'servicing');
  }

  get IssuesCollectionRef() {
    return collection(this.firestore, 'issues');
  }

  get InvoicesCollectionRef() {
    return collection(this.firestore, 'invoices');
  }

  get StorageCollectionRef() {
    return collection(this.firestore, 'storage');
  }

  get MachinesCollectionRef() {
    return collection(this.firestore, 'machines');
  }

  get TransactionsCollectionRef() {
    return collection(this.firestore, 'transactions');
  }

  public addNewUserDoc(data: User): Promise<void> {
    return setDoc(doc(this.UsersCollectionRef.firestore, 'users', data.id), data);
    // addDoc(this.UsersCollectionRef, data);
  }

  /**
   * Exists for the registration rollback, which has to undo the profile as well as the Auth
   * account: rolling back only the account left `/users/<uid>` behind, and a retry then minted a
   * fresh uid and a second profile for the same person.
   */
  public deleteUserDoc(uid: string): Promise<void> {
    return deleteDoc(doc(this.UsersCollectionRef.firestore, 'users', uid));
  }

  public addNewUserAddressDoc(data: Address, userId: string): Promise<DocumentReference<DocumentData, DocumentData>> {
    return addDoc(this.getAddressesCollectionRef(userId), data);
  }

  public addNewIssuesDoc(data: Issue): Promise<DocumentReference<DocumentData, DocumentData>> {
    return addDoc(this.IssuesCollectionRef, data);
  }

  public addNewInvoicesDoc(data: Invoice): Promise<DocumentReference<DocumentData, DocumentData>> {
    return addDoc(this.InvoicesCollectionRef, data);
  }

  public addNewMachinesDoc(data: Machine): Promise<DocumentReference<DocumentData, DocumentData>> {
    return addDoc(this.MachinesCollectionRef, data);
  }

  public addNewTransactionsDoc(data: Transaction): Promise<DocumentReference<DocumentData, DocumentData>> {
    return addDoc(this.TransactionsCollectionRef, data);
  }

  public addNewServicingDoc(data: Servicing): Promise<DocumentReference<DocumentData, DocumentData>> {
    return addDoc(this.ServicingCollectionRef, data);
  }

  public async getAuthenticatedUser(): Promise<CustomUser | null> {
    const user = this.authService.currentUser;
    if (user) {
      const docData = (await getDoc(doc(this.firestore, `users/${this.authService.currentUser?.uid}`))).data();

      return { ...user, isAdmin: docData?.['type'] === 'ADMIN' };
    }
    return user;
  }

  public async getDDOV(
    dropdownValues: DropdownValueGetter | undefined,
    collection = 'machines',
  ): Promise<KeyValueArray | KeyValueGroupArray | undefined> {
    if (
      !Array.isArray(dropdownValues) &&
      dropdownValues?.dropdownCollectionField &&
      !dropdownValues?.parentCollectionField
    ) {
      const splitValue = dropdownValues.dropdownCollectionField.split('/');
      if (splitValue.length > 1) {
        const fields = await this.getFieldValues(splitValue[0], splitValue[1]);
        const returnFields = fields?.map((x) => ({
          key: x[splitValue[1]],
          value: dropdownValues?.isReference ? x.parent : x.parent.path,
        }));
        if (dropdownValues.addEmpty) {
          returnFields?.push({ key: this.empty(), value: '' });
        }
        return FirestoreService.removeDuplicates(returnFields);
      } else {
        console.warn('this neeeds to be refactored', dropdownValues);
        const fieldValues = await this.getFieldValues(collection, dropdownValues.dropdownCollectionField);
        const filedResult = fieldValues?.map((x) => ({
          key: x[dropdownValues.dropdownCollectionField].toString(),
          value: dropdownValues?.isReference ? x.parent : x[dropdownValues.dropdownCollectionField].toString(),
        }));
        return FirestoreService.removeDuplicates(filedResult);
      }
    } else if (
      !Array.isArray(dropdownValues) &&
      dropdownValues?.dropdownCollectionField &&
      dropdownValues?.parentCollectionField
    ) {
      const splitedParentGetter = dropdownValues.parentCollectionField.split('/');
      const splitedDropsGetter = dropdownValues.dropdownCollectionField.split('/');

      const fields = await this.getFieldValues(splitedDropsGetter[0], splitedDropsGetter[1]);
      const addressValueMap: Map<
        string,
        { name: string; value: Array<{ key: string; value: string | DocumentReference }> }
      > = new Map<string, { name: string; value: Array<{ key: string; value: string }> }>();

      for (const key in fields) {
        if (Object.prototype.hasOwnProperty.call(fields, key)) {
          const x = fields[key];
          const id = x.parent?.parent?.parent?.id;
          const path = x.parent.path;
          const addressValue = addressValueMap.get(id ?? '');
          if (addressValue) {
            addressValue.value.push({
              key: x[splitedDropsGetter[1]].toString(),
              value: dropdownValues?.isReference ? x.parent : path.toString(),
            });
          } else {
            const user = await getDoc(doc(this.firestore, splitedParentGetter[0], id ?? ''));
            addressValueMap.set(id ?? '', {
              name: user.data()?.[splitedParentGetter[1]],
              value: [
                {
                  key: x[splitedDropsGetter[1]].toString(),
                  value: dropdownValues?.isReference ? x.parent : path.toString(),
                },
              ],
            });
          }
        }
      }
      const resultArray: Array<{ name: string; value: Array<{ key: string; value: string | DocumentReference }> }> = [];
      addressValueMap.forEach((x) => resultArray.push(x));
      if (dropdownValues.addEmpty) {
        resultArray.push({ name: this.empty(), value: [{ key: this.empty(), value: '' }] });
      }
      return resultArray;
    } else if (Array.isArray(dropdownValues)) {
      return (dropdownValues as KeyValueArray)?.map(({ key, value }) => ({ key, value }));
    }
    return undefined;
  }

  private static removeDuplicates(
    array: Array<{
      key: string | number;
      value: string | number | Date | DocumentReference<DocumentData, DocumentData>;
    }>,
  ): Array<{ key: string | number; value: string | DocumentReference<DocumentData, DocumentData> | number | Date }> {
    const unique = new Map<
      number | string,
      { key: string | number; value: string | DocumentReference<DocumentData, DocumentData> | number | Date }
    >();

    array.forEach((item) => {
      if (!unique.has(item.key)) {
        unique.set(item.key, item);
      }
    });

    return Array.from(unique.values());
  }

  public async definitionObjectCreateMapper(field: ColumnDefinition, collectionName: string): Promise<InputFieldData> {
    return {
      id: field.id,
      name: field.name,
      options: {
        isBulk: field.canBulk,
        isPlain: field.isPlain,
        isRequired: field.isRequired,
        defaultValue: field.default,
        isReadOnly: field.isReadOnly,
        disableMapper: field.disableMapper,
        type: field.type,
        dropdownOptionsValue: await this.getDDOV(field.dropdownValues, collectionName),
      },
    };
  }
  // skipcq: JS-0105
  public definitionObjectFilterMapper(field: ColumnDefinition): Promise<InputFieldData> {
    return Promise.resolve({
      id: field.id,
      name: field.name,
      options: {
        isBulk: field.canBulk,
        isPlain: field.isPlain,
        isRequired: field.isRequired,
        defaultValue: field.default,
        isReadOnly: field.isReadOnly,
        disableMapper: field.disableMapper,
        type: field.type,
      },
    });
  }

  public async definitionObjcetEditMapper(field: {
    selectedFieldData: unknown;
    selectedFieldDefininition: { definition: IDeptDefinition; data: ColumnDefinition };
  }) {
    return {
      id: field.selectedFieldDefininition.data.id,
      name: field.selectedFieldDefininition.data.name,
      isReadOnly: field.selectedFieldDefininition.data.isReadOnly ?? false,
      default: field.selectedFieldDefininition.data.default,
      options: {
        isPlain: field.selectedFieldDefininition.data.isPlain,
        type: field.selectedFieldDefininition.data.type ?? 'textbox',
        isReadOnly: field.selectedFieldDefininition.data.isReadOnly,
        isRequired: field.selectedFieldDefininition.data.isRequired,
        disableMapper: field.selectedFieldDefininition.data.disableMapper,
        dropdownOptionsValue: await this.getDDOV(field.selectedFieldDefininition.data.dropdownValues),
        value: field.selectedFieldData,
      },
    } as InputFieldData;
  }
}
